/**
 * CR055: Hook for source health monitoring
 */

import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";

export interface SourceHealthCheck {
  id: string;
  run_id: string | null;
  merchant_id: string;
  source_id: string;
  source_url: string;
  status: "success" | "failed";
  failure_reason: string | null;
  http_status: number | null;
  text_length: number | null;
  extraction_quality_score: number | null;
  checked_at: string;
}

export interface SourceHealthSummary {
  source_id: string;
  source_url: string;
  merchant_id: string;
  merchant_name: string;
  total_checks: number;
  success_count: number;
  failure_count: number;
  failure_rate: number;
  avg_text_length: number;
  avg_quality_score: number;
  last_checked_at: string | null;
  common_failure_reasons: string[];
}

export interface MerchantHealthSummary {
  merchant_id: string;
  merchant_name: string;
  active_sources: number;
  failing_sources: number;
  low_quality_sources: number;
}

export interface HealthConfig {
  failure_threshold_pct: number;
  min_text_length_success: number;
  min_quality_score_success: number;
  lookback_hours: number;
}

const DEFAULT_CONFIG: HealthConfig = {
  failure_threshold_pct: 30,
  min_text_length_success: 200,
  min_quality_score_success: 0.4,
  lookback_hours: 24,
};

export function useSourceHealth(config: Partial<HealthConfig> = {}) {
  const [healthData, setHealthData] = useState<SourceHealthSummary[]>([]);
  const [merchantHealth, setMerchantHealth] = useState<MerchantHealthSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const effectiveConfig = { ...DEFAULT_CONFIG, ...config };

  const fetchHealthData = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      // Get health checks from last N hours
      const cutoffDate = new Date();
      cutoffDate.setHours(cutoffDate.getHours() - effectiveConfig.lookback_hours);
      
      const { data: checks, error: checksError } = await supabase
        .from("merchant_source_checks")
        .select("*")
        .gte("checked_at", cutoffDate.toISOString())
        .order("checked_at", { ascending: false });

      if (checksError) throw checksError;

      // Get all sources for reference
      const { data: sources, error: sourcesError } = await supabase
        .from("merchant_sources")
        .select("id, source_url, merchant_id, merchant_name, is_active, status");

      if (sourcesError) throw sourcesError;

      // Build source health summaries
      const sourceMap = new Map<string, SourceHealthSummary>();
      
      // Initialize with all sources
      (sources || []).forEach((source: any) => {
        sourceMap.set(source.id, {
          source_id: source.id,
          source_url: source.source_url,
          merchant_id: source.merchant_id,
          merchant_name: source.merchant_name || source.merchant_id,
          total_checks: 0,
          success_count: 0,
          failure_count: 0,
          failure_rate: 0,
          avg_text_length: 0,
          avg_quality_score: 0,
          last_checked_at: null,
          common_failure_reasons: [],
        });
      });

      // Aggregate check data
      const failureReasonsBySource = new Map<string, Map<string, number>>();
      
      (checks || []).forEach((check: SourceHealthCheck) => {
        const summary = sourceMap.get(check.source_id);
        if (!summary) return;
        
        summary.total_checks++;
        
        if (check.status === "success") {
          summary.success_count++;
        } else {
          summary.failure_count++;
          
          // Track failure reasons
          if (check.failure_reason) {
            if (!failureReasonsBySource.has(check.source_id)) {
              failureReasonsBySource.set(check.source_id, new Map());
            }
            const reasons = failureReasonsBySource.get(check.source_id)!;
            reasons.set(check.failure_reason, (reasons.get(check.failure_reason) || 0) + 1);
          }
        }
        
        if (check.text_length) {
          summary.avg_text_length += check.text_length;
        }
        
        if (check.extraction_quality_score) {
          summary.avg_quality_score += check.extraction_quality_score;
        }
        
        if (!summary.last_checked_at || check.checked_at > summary.last_checked_at) {
          summary.last_checked_at = check.checked_at;
        }
      });

      // Finalize averages and failure rates
      sourceMap.forEach((summary, sourceId) => {
        if (summary.total_checks > 0) {
          summary.failure_rate = (summary.failure_count / summary.total_checks) * 100;
          summary.avg_text_length = summary.avg_text_length / summary.total_checks;
          summary.avg_quality_score = summary.avg_quality_score / summary.total_checks;
        }
        
        // Get top failure reasons
        const reasons = failureReasonsBySource.get(sourceId);
        if (reasons) {
          summary.common_failure_reasons = Array.from(reasons.entries())
            .sort((a, b) => b[1] - a[1])
            .slice(0, 3)
            .map(([reason]) => reason);
        }
      });

      const healthSummaries = Array.from(sourceMap.values());
      setHealthData(healthSummaries);

      // Build merchant health summaries
      const merchantMap = new Map<string, MerchantHealthSummary>();
      
      healthSummaries.forEach((summary) => {
        const existing = merchantMap.get(summary.merchant_id) || {
          merchant_id: summary.merchant_id,
          merchant_name: summary.merchant_name,
          active_sources: 0,
          failing_sources: 0,
          low_quality_sources: 0,
        };
        
        const source = (sources || []).find((s: any) => s.id === summary.source_id);
        if (source?.status === "active" || source?.is_active) {
          existing.active_sources++;
        }
        
        if (summary.failure_rate >= effectiveConfig.failure_threshold_pct) {
          existing.failing_sources++;
        }
        
        if (summary.avg_text_length > 0 && summary.avg_text_length < effectiveConfig.min_text_length_success) {
          existing.low_quality_sources++;
        }
        
        merchantMap.set(summary.merchant_id, existing);
      });

      setMerchantHealth(Array.from(merchantMap.values()));
    } catch (err) {
      const message = err instanceof Error ? err.message : "Failed to fetch health data";
      setError(message);
    } finally {
      setLoading(false);
    }
  }, [effectiveConfig.lookback_hours, effectiveConfig.failure_threshold_pct, effectiveConfig.min_text_length_success]);

  useEffect(() => {
    fetchHealthData();
  }, [fetchHealthData]);

  // Filtered views
  const getFailingSources = () => {
    return healthData.filter(
      (s) => s.total_checks > 0 && s.failure_rate >= effectiveConfig.failure_threshold_pct
    );
  };

  const getLowQualitySources = () => {
    return healthData.filter(
      (s) => s.total_checks > 0 && 
        s.avg_text_length > 0 && 
        s.avg_text_length < effectiveConfig.min_text_length_success
    );
  };

  const getMerchantsWithNoActiveSources = () => {
    return merchantHealth.filter((m) => m.active_sources < 1);
  };

  const getMerchantsWithIssues = () => {
    return merchantHealth.filter(
      (m) => m.failing_sources > 0 || m.low_quality_sources > 0
    );
  };

  return {
    healthData,
    merchantHealth,
    loading,
    error,
    config: effectiveConfig,
    fetchHealthData,
    getFailingSources,
    getLowQualitySources,
    getMerchantsWithNoActiveSources,
    getMerchantsWithIssues,
  };
}
